import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import { FormActions } from './FormActions';
import { useGetUserQuery } from '../../../../api';
import { PasswordFields } from './PasswordFields';
import { UserInfoFields } from './UserInfoFields';
import { MultiFactorAuthFields } from './MultiFactorAuthFields';
import { handleError, getSessionVar, setSessionVar } from '../../../../lib/helpers';
import { Col, Divider, Row, AppModal } from '../../../../components';
import { useNotificationContext } from '../../../../providers/NotificationProvider';
import { useUpdateAccountMutation, useChangePasswordMutation, useUpdateMFASettingsMutation } from '../../../../api';
import './manageAccountModal.scss';

export const passwordRequirementText =
    "At least 8 characters, including lower case and/or upper case, numbers and symbols. It cannot be the same as a user's previous four passwords.";

const updateDetails = async (params) => {
    const { id, first_name, last_name, updateAccount, notifySuccess, notifyError } = params;
    try {
        const res = await updateAccount({ id, first_name, last_name });
        if (res?.error) {
            handleError(res.error, notifyError, 'Error updating account');
        } else {
            notifySuccess({ message: 'Account details have been successfully updated.' });
        }
    } catch (err) {
        handleError(err, notifyError, 'Error updating account');
        throw err;
    }
};

const updatePasswordOnly = async (params) => {
    const { id, form, current_password, new_password, confirm_password, updatePassword, notifySuccess, notifyError } =
        params;
    try {
        await form.validateFields(['current_password', 'new_password', 'confirm_password']);
        const res = await updatePassword({ id, current_password, new_password, confirm_password });

        if (res?.error) {
            handleError(res.error, notifyError, 'Error updating password');
            throw res.error;
        }

        notifySuccess({ message: 'Account password has been successfully updated.' });
    } catch (err) {
        handleError(err, notifyError, 'Error updating password');
        throw err;
    }
};

const updateMFASettings = async (params) => {
    const { mfa_enabled, mfa_method, phone_number, updateMFA, id, notifySuccess, notifyError } = params;
    let mfaDetails = {};
    try {
        if (mfa_enabled) {
            mfaDetails.mfa_method = mfa_method;
            // This will also delete cell phone number not in use (future GDPR compliance):
            mfaDetails.cellphone_number = mfa_method === 'sms' ? phone_number : null;
        }
        await updateMFA({ mfa_details: mfaDetails, userId: id });
        notifySuccess({ message: 'MFA settings have been successfully updated.' });

        // Update user in session storage
        let user = {
            ...getSessionVar('user'),
            mfa_enabled,
            mfa_method,
            mfa_phone_number: mfaDetails.cellphone_number,
        };
        setSessionVar('user', user);
    } catch (err) {
        handleError(err, notifyError, 'Error updating MFA settings');
        throw err;
    }
};

export const ManageAccountModal = React.memo(({ open, onCancel }) => {
    const [form] = Form.useForm();
    const { notifyError, notifySuccess } = useNotificationContext();
    const id = sessionStorage.getItem('loggedInUserId');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const selectedMfaMethod = Form.useWatch('mfa_method', form);

    const { data: userRecord = {} } = useGetUserQuery({ id }, { skip: !id });
    const [updateAccount, { isLoading: isLoadingUpdate }] = useUpdateAccountMutation();
    const [updatePassword, { isLoading: isLoadingUpdatePass }] = useChangePasswordMutation();
    const [updateMFA] = useUpdateMFASettingsMutation();

    useEffect(() => {
        if (open && userRecord && form) {
            const fieldValues = {
                username: userRecord.email,
                first_name: userRecord.first_name,
                last_name: userRecord.last_name,
            };

            // Only set mfa_method value on the form if MFA is enabled
            if (userRecord.mfa_enabled) {
                fieldValues.mfa_method = userRecord.mfa_method || 'email';
            }
            form.setFieldsValue(fieldValues);
        }

        if (!open) {
            setIsPhoneVerified(false);
        }
    }, [open, userRecord, form]);

    const isSaveDisabled = userRecord.mfa_enabled && selectedMfaMethod === 'sms' && !isPhoneVerified;

    const onSubmit = useCallback(
        async (values) => {
            const {
                first_name,
                last_name,
                current_password,
                new_password,
                confirm_password,
                mfa_method,
                phone_number,
            } = values;

            const hasPasswordChange = current_password || new_password || confirm_password;

            try {
                if (hasPasswordChange) {
                    await updatePasswordOnly({
                        id,
                        form,
                        current_password,
                        new_password,
                        confirm_password,
                        updatePassword,
                        notifySuccess,
                        notifyError,
                    });
                }

                if (userRecord.mfa_enabled && mfa_method !== userRecord.mfa_method) {
                    await updateMFASettings({
                        mfa_enabled: userRecord.mfa_enabled,
                        mfa_method,
                        phone_number,
                        updateMFA,
                        id,
                        notifySuccess,
                        notifyError,
                    });
                }

                await updateDetails({
                    id,
                    first_name,
                    last_name,
                    updateAccount,
                    notifySuccess,
                    notifyError,
                });

                onCancel();
            } catch {}
        },
        [form, id, onCancel, isPhoneVerified],
    );

    return (
        <AppModal
            title="Manage Account"
            className={'manage-account-modal'}
            onCancel={onCancel}
            open={open}
            width={600}
            closable
            maskClosable
            footer={null}
        >
            <Form form={form} onFinish={onSubmit} layout="vertical">
                <Row gutter={[12]}>
                    <UserInfoFields />
                    <Col span={24}>
                        <Divider />
                    </Col>
                    <PasswordFields form={form} />
                    {userRecord.mfa_enabled && (
                        <MultiFactorAuthFields form={form} userId={id} onVerificationChange={setIsPhoneVerified} />
                    )}
                </Row>
                <FormActions
                    onCancel={onCancel}
                    isLoading={isLoadingUpdate || isLoadingUpdatePass}
                    isSaveDisabled={isSaveDisabled}
                />
            </Form>
        </AppModal>
    );
});
